import { Link } from "@/lib/router";
import { Camera, ListChecks, MessagesSquare, Network, ShieldCheck } from "lucide-react";
import { BrandMark } from "@/components/brand-mark";
import { buttonVariants } from "@/components/ui/button";
import { LEGAL } from "@/lib/legal/config";
import { cn } from "@/lib/utils";

const STEPS = [
  { icon: Camera, title: "찍어서 올리기", body: "손글씨 설교 노트를 사진으로 올려요. 따로 옮겨 적을 필요가 없어요." },
  { icon: Network, title: "AI가 정리하기", body: "요약과 마인드맵, 이번 주에 해볼 실천 후보를 만들어 드려요." },
  { icon: ListChecks, title: "실천하고 돌아보기", body: "실천할 것을 정하고, 한 주가 지나면 했는지 기록하며 돌아봐요." },
] as const;

const FEATURES = [
  {
    icon: Network,
    title: "요약과 마인드맵",
    body: "긴 노트를 한눈에 보는 요약, 표와 목록으로 나뉜 정리, 흐름을 보여주는 마인드맵을 함께 봐요.",
  },
  {
    icon: ListChecks,
    title: "이번 주 실천",
    body: "노트마다 실천 후보를 받아 이번 주 실천으로 정하세요. 일요일에 시작하는 한 주 단위로 진행 상황을 기록해요.",
  },
  {
    icon: MessagesSquare,
    title: "물어보면 찾아주는 검색",
    body: "제목이 기억나지 않아도 요즘의 마음이나 단어로 물어보면 지난 노트를 찾아 보여드려요.",
  },
  {
    icon: ShieldCheck,
    title: "나만 보는 기록",
    body: "노트는 내 계정에서만 볼 수 있어요. 개별 노트 삭제와 회원 탈퇴로 사진을 포함한 모든 기록을 지울 수 있어요.",
  },
] as const;

/**
 * 로그인하지 않은 방문자가 "/"에서 보는 랜딩 페이지 (middleware가 이 경로로 rewrite한다).
 * 아직 없는 기능은 소개하지 않는다.
 */
export default function LandingPage() {
  return (
    <div className="flex flex-1 flex-col">
      <header className="mx-auto flex w-full max-w-5xl items-center justify-between px-6 py-5">
        <BrandMark className="text-lg" />
        <nav className="flex items-center gap-2" aria-label="시작">
          <Link href="/auth/login" className={buttonVariants({ variant: "ghost" })}>
            로그인
          </Link>
          <Link href="/auth/sign-up" className={cn(buttonVariants(), "rounded-full")}>
            시작하기
          </Link>
        </nav>
      </header>

      <main className="flex-1">
        <section className="mx-auto flex max-w-3xl flex-col items-center gap-6 px-6 pb-20 pt-16 text-center md:pt-24">
          <p className="text-sm font-medium tracking-wide text-muted-foreground">{LEGAL.slogan}</p>
          <h1 className="text-4xl font-semibold leading-tight tracking-tight md:text-5xl">
            들은 말씀을,
            <br />
            사진 한 장으로 남기세요
          </h1>
          <p className="max-w-xl text-muted-foreground md:text-lg">
            손글씨 설교 노트를 찍으면 요약과 마인드맵, 이번 주 실천까지 정리해 드려요. 쌓인 노트는 물어보면
            찾아드립니다.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link href="/auth/sign-up" className={cn(buttonVariants({ size: "lg" }), "h-12 rounded-full px-8 text-base")}>
              무료로 시작하기
            </Link>
            <Link
              href="/auth/login"
              className={cn(buttonVariants({ size: "lg", variant: "outline" }), "h-12 rounded-full px-8 text-base")}
            >
              로그인
            </Link>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-6 pb-20" aria-labelledby="how">
          <h2 id="how" className="mb-8 text-center text-2xl font-semibold tracking-tight">
            이렇게 쓰여요
          </h2>
          <ol className="grid gap-4 md:grid-cols-3">
            {STEPS.map((step, i) => (
              <li key={step.title} className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-6">
                <span className="flex size-10 items-center justify-center rounded-full bg-muted">
                  <step.icon className="size-5" />
                </span>
                <h3 className="font-semibold">
                  {i + 1}. {step.title}
                </h3>
                <p className="text-sm text-muted-foreground">{step.body}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="mx-auto max-w-5xl px-6 pb-20" aria-labelledby="features">
          <h2 id="features" className="mb-8 text-center text-2xl font-semibold tracking-tight">
            말씀을 삶으로 잇는 기능
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            {FEATURES.map((f) => (
              <div key={f.title} className="flex gap-4 rounded-2xl border border-border p-6">
                <f.icon className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
                <div className="flex flex-col gap-1">
                  <h3 className="font-semibold">{f.title}</h3>
                  <p className="text-sm text-muted-foreground">{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-3xl px-6 pb-24 text-center">
          <p className="text-sm text-muted-foreground">
            AI가 만든 요약과 실천 후보는 참고용이에요. 사진의 글을 잘못 읽을 수 있으니 원본과 함께 확인해 주세요.
            특정 교단이나 교리를 대표하지 않는 개인 기록 도구입니다.
          </p>
          <Link href="/auth/sign-up" className={cn(buttonVariants({ size: "lg" }), "mt-8 h-12 rounded-full px-8 text-base")}>
            지금 시작하기
          </Link>
        </section>
      </main>

      <footer className="border-t border-border px-6 py-8 text-center text-xs text-muted-foreground">
        <nav className="mb-2 flex justify-center gap-4" aria-label="정책">
          <Link href="/terms" className="underline underline-offset-4">이용약관</Link>
          <Link href="/privacy" className="underline underline-offset-4">개인정보 처리방침</Link>
          <Link href="/scripture-copyright" className="underline underline-offset-4">성경 본문 저작권</Link>
        </nav>
        <p>
          © {LEGAL.serviceName} · 운영자 {LEGAL.operatorName} · {LEGAL.contactEmail}
        </p>
      </footer>
    </div>
  );
}
